import { Box, Button, CircularProgress, Typography, Divider } from "@mui/material";
import { useEffect, useRef } from "react";
import { Map as MapIcon } from "@mui/icons-material";
import { GoogleMap } from "@react-google-maps/api";
import { Circle as CircleOverlay } from "../../theme/maps";
import { WorkforceMap } from "../employees/components/WorkforceMap";
import { BaseModalComponent } from "../common/BaseModalComponent";

// ─── Constants ────────────────────────────────────────────────────────────────

const mapContainerStyle = {
  width: "100%",
  height: "260px",
  borderRadius: "8px",
  marginTop: "8px",
  marginBottom: "12px",
};

const circleOptions = {
  strokeColor: "#FF0000",
  strokeOpacity: 0.8,
  strokeWeight: 2,
  fillColor: "#FF0000",
  fillOpacity: 0.35,
};

const mapOptions = {
  fullscreenControl: false,
  mapTypeControl: false,
  streetViewControl: false,
};

// ─── Component ────────────────────────────────────────────────────────────────

export const DeviceStatusWithMap = ({ selectedMode, workforce, device, lastConexion, handleUnLinkDevice, waitingForLocation }) => {
  const workforceFullName = [workforce?.firstName, workforce?.lastName].filter(Boolean).join(" ");
  const mapRef = useRef(null);

  useEffect(() => {
    if (!lastConexion || !mapRef.current) return;
    mapRef.current.panTo(lastConexion);
    mapRef.current.setZoom(16);
  }, [lastConexion?.lat, lastConexion?.lng]);

  return (
    <>
      <Box sx={{ position: "relative" }}>
        {lastConexion ? (
          <GoogleMap
            mapContainerStyle={mapContainerStyle}
            center={lastConexion}
            zoom={16}
            options={mapOptions}
            onLoad={(map) => {
              mapRef.current = map;
              map.panTo(lastConexion);
              map.setZoom(16);
            }}
          >
            <CircleOverlay center={lastConexion} radius={100} options={circleOptions} />
          </GoogleMap>
        ) : (
          <Box
            sx={{
              ...mapContainerStyle,
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              gap: 1.5,
              bgcolor: "var(--fx-surface-sunken, #F8FAFC)",
              border: "1px solid var(--fx-border-subtle, #E2E8F0)",
            }}
          >
            <CircularProgress size={28} sx={{ color: "#f97316" }} />
            <Typography sx={{ fontSize: "0.85rem", color: "var(--fx-surface-textsecondary, #64748B)" }}>Looking for the current location...</Typography>
          </Box>
        )}
      </Box>

      <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
        <Box sx={{ flex: 1 }}>
          <BaseModalComponent
            modalKey="Workforce-Tracker-Map"
            modalLabel={`Worker ${workforceFullName}`}
            elementTrigger={
              <Button
                variant="outlined"
                size="small"
                fullWidth
                startIcon={<MapIcon fontSize="small" />}
                sx={{
                  py: 0.75,
                  color: "#f97316",
                  borderColor: "#f97316",
                  backgroundColor: "var(--fx-colors-warning-50, #FFF7ED)",
                  borderRadius: 2,
                  textTransform: "none",
                  fontWeight: 600,
                  fontSize: "0.875rem",
                  "&:hover": {
                    backgroundColor: "var(--fx-colors-warning-100, #FFEDD5)",
                    borderColor: "#ea580c",
                  },
                }}
              >
                View Location Map
              </Button>
            }
          >
            <WorkforceMap mapHeight="100%" employee={workforce} device={device} currentLocation={lastConexion} />
          </BaseModalComponent>
        </Box>

        <Divider orientation="vertical" flexItem />

        <Box sx={{ flex: 1 }}>
          <Button
            variant="outlined"
            size="small"
            fullWidth
            onClick={handleUnLinkDevice}
            sx={{
              py: 0.75,
              color: "#ef4444",
              borderColor: "#ef4444",
              backgroundColor: "var(--fx-colors-danger-50, #FEF2F2)",
              borderRadius: 2,
              textTransform: "none",
              fontWeight: 600,
              fontSize: "0.875rem",
              "&:hover": {
                backgroundColor: "var(--fx-colors-danger-100, #FEE2E2)",
                borderColor: "#dc2626",
              },
            }}
          >
            Unlink Device
          </Button>
        </Box>
      </Box>
    </>
  );
};
